import { Pause, PlayArrow, VolumeOff, VolumeUp } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Grid,
  Grow,
  IconButton,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { ReactComponent as NoDesigns } from "../.././assets/images/NoRecommended.svg";
import VerticalCard from "../../components/ui/cards/verticalCard/VerticalCard";
import VerticalCardSkeleton from "../../components/ui/skeletons/verticalCardSkeleton";
import { useGlobalContext } from "../../context/GlobalContext";
import { getIcon } from "../../utils/iconConfig";
import {
  fetchUspandVideo,
  useRecommendedDesigns,
  useRelevantSets,
} from "./api/HomeScreenApi";

const HomeScreen = () => {
  const {
    selectedCard,
    handleAddToCart,
    cartData,
    payload,
    videoModal,
    setVideoModal,
  } = useGlobalContext();

  const { ref: ScrollRef, inView: LoaderInView } = useInView({
    threshold: 0.6,
    triggerOnce: false,
  });

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);

  const handleVideoModalOpen = (data) => {
    setVideoModal({ isOpen: true, data, isScanned: false });
  };

  const handleClose = () => {
    setVideoModal((prev) => ({
      ...prev,
      isOpen: false,
      isScanned: false,
    }));
  };

  const fetchVideoData = useCallback(async () => {
    try {
      if (videoModal.isScanned) return;

      const { videoData } = await fetchUspandVideo(selectedCard);
      const hasVideoData = videoData?.length > 0;
      if (hasVideoData) {
        setVideoModal((prev) => ({
          ...prev,
          isOpen: false,
          data: videoData?.[0] || "",
        }));
        setIsMuted(false);
      } else {
        setVideoModal((prev) => ({
          ...prev,
          isOpen: false,
          data: null,
        }));
      }
    } catch (error) {
      console.error("Error fetching video:", error);
      setVideoModal((prev) => ({
        ...prev,
        isOpen: false,
        data: null,
      }));
    }
  }, [selectedCard, videoModal?.isScanned]);

  useEffect(() => {
    if (selectedCard) {
      fetchVideoData();
    }
  }, [selectedCard]);

  const [showAllRelevantSets, setShowAllRelevantSets] = useState(false);

  const clientCode = useMemo(() => {
    const ClientData = JSON.parse(localStorage.getItem("ClientData"));
    return ClientData?.clientCode;
  }, []);

  // Function to update progress
  const updateProgress = () => {
    const video = videoRef.current;
    if (video && video.duration) {
      const newProgress = (video.currentTime / video.duration) * 100;
      setProgress(newProgress);
    }
  };

  // Attach event listener to update progress
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted; // Use the state instead of current value
      setIsMuted(!isMuted); // Update the state
    }
  };

  const handleProgressClick = (e) => {
    const video = videoRef.current;
    if (!video) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * video.duration;
    video.currentTime = newTime;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.addEventListener("timeupdate", updateProgress);

    return () => {
      video.removeEventListener("timeupdate", updateProgress);
    };
  }, [isMuted]);

  // Reset progress when the modal opens
  useEffect(() => {
    if (videoModal.isOpen || videoModal?.isScanned) {
      setProgress(0);
    }
  }, [videoModal.isOpen, videoModal?.isScanned]);

  const { data: relevantData, isLoading: loadingRelevant } =
    useRelevantSets(selectedCard);

  const {
    data: recommendedData,
    fetchNextPage: fetchNextRecommended,
    hasNextPage: hasNextRecommended,
    isLoading: loadingRecommended,
    isFetchingNextPage: fetchingMoreRecommended,
  } = useRecommendedDesigns({
    clientCode,
    styleNumber: selectedCard,
    avgDiaWeight: parseFloat(payload?.avgDiaWeight ?? 0),
    avgNetWeight: parseFloat(payload?.avgNetWeight ?? 0),
  });

  // Memoized cart IDs for filtering
  const cartIds = useMemo(
    () => new Set(cartData?.map((item) => item._id) || []),
    [cartData]
  );

  // Memoized filter function
  const filterOutCartItems = useMemo(
    () => (items) => {
      if (!items) return [];
      return items.filter((item) => !cartIds.has(item._id));
    },
    [cartIds]
  );

  // Memoized data processing
  const relevantSets = useMemo(
    () => relevantData?.pages?.flatMap((page) => page?.relevantSets) || [],
    [relevantData]
  );

  const recommendedDesigns = useMemo(
    () => recommendedData?.pages?.flatMap((page) => page?.designs) || [],
    [recommendedData]
  );

  const filteredRelevantSets = useMemo(
    () => filterOutCartItems(relevantSets),
    [relevantSets, filterOutCartItems]
  );

  const filteredRecommendedDesigns = useMemo(
    () => filterOutCartItems(recommendedDesigns),
    [recommendedDesigns, filterOutCartItems]
  );

  useEffect(() => {
    if (LoaderInView && hasNextRecommended && !fetchingMoreRecommended) {
      fetchNextRecommended();
    }
  }, [
    LoaderInView,
    hasNextRecommended,
    fetchingMoreRecommended,
    fetchNextRecommended,
  ]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  if (loadingRelevant || loadingRecommended) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="success" />
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          borderTop: ".1vw solid #0d0d0d",
          borderLeft: ".1vw solid #0d0d0d",
          width: "100%",
          height: "100%",
          padding: "1vw 2vw",
          overflowY:
            videoModal?.isOpen || videoModal?.isScanned ? "hidden" : "scroll",
          position: "relative",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "1.1vw",
            right: "5vw",
            cursor: "pointer",
            zIndex: 5,
            visibility: !videoModal?.data ? "hidden" : "visible",
          }}
          onClick={() => handleVideoModalOpen(videoModal?.data)}
        >
          {getIcon("Play")}
        </span>

        {/* Dark Overlay */}
        {(videoModal.isOpen || videoModal?.isScanned) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              zIndex: 2,
            }}
          />
        )}

        {/* Relevant Sets */}
        {filteredRelevantSets?.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1vw",
              position: "relative",
            }}
          >
            <Typography fontSize="1vw" fontWeight="300" fontFamily="Poppins">
              Relevant Sets
            </Typography>

            <Box>
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                sx={{
                  flex: "1 1 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "2vw",
                  rowGap: "1.5vw",
                }}
              >
                {(showAllRelevantSets
                  ? filteredRelevantSets
                  : filteredRelevantSets.slice(0, 5)
                ).map((set, index) => {
                  const isLastVisibleWithMore =
                    !showAllRelevantSets &&
                    index === 4 &&
                    filteredRelevantSets.length > 5;

                  return (
                    <div
                      key={set._id}
                      style={{
                        position: "relative",
                        cursor: isLastVisibleWithMore ? "pointer" : "default",
                      }}
                      onClick={() => {
                        if (isLastVisibleWithMore) setShowAllRelevantSets(true);
                      }}
                    >
                      <VerticalCard
                        data={set}
                        color={"#0D0D0D"}
                        allData={filteredRelevantSets}
                        onAdd={() => handleAddToCart(set, "Relevant Set")}
                      />

                      {isLastVisibleWithMore && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            color: "#fff",
                            fontSize: "1.2vw",
                            fontWeight: "600",
                            fontFamily: "Poppins",
                            display: "flex",
                            // alignItems: "center",
                            paddingTop: "5vw",
                            justifyContent: "center",
                            // borderRadius: "1vw",
                            zIndex: 1,
                          }}
                        >
                          +{filteredRelevantSets.length - 5}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Grid>
            </Box>
          </div>
        )}

        {/* Recommended Designs */}
        {filteredRecommendedDesigns?.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1vw",
              position: "relative",
              marginTop: "1vw",
            }}
          >
            <Typography
              fontSize="1vw"
              fontWeight="300"
              fontFamily="Poppins"
              marginTop={"1vw"}
            >
              Recommended Designs
            </Typography>
            <Box>
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                sx={{
                  flex: "1 1 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "2vw",
                  rowGap: "1.5vw",
                }}
              >
                {filteredRecommendedDesigns?.map((set) => (
                  <VerticalCard
                    key={set?._id}
                    data={set}
                    color={"#0D0D0D"}
                    onAdd={() => {
                      // fetchNextRecommended();
                      handleAddToCart(set, "Recommended Designs");
                    }}
                    allData={filteredRecommendedDesigns}
                  />
                ))}
                {hasNextRecommended && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    marginTop: "1.5vw",
                    alignItems: "center",
                  }}
                  ref={ScrollRef}
                >
                  {fetchingMoreRecommended && (
                    <VerticalCardSkeleton value={5} gap={"2vw"} />
                  )}
                </div>
              )}
              </Grid>
            </Box>
          </div>
        )}

        {filteredRelevantSets?.length === 0 &&
          filteredRecommendedDesigns?.length === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <NoDesigns />
                <Typography
                  sx={{
                    color: "#B3B3B3",
                    fontFamily: "Poppins",
                    fontSize: ".7vw",
                    fontWeight: 400,
                  }}
                >
                  No Data Found
                </Typography>
              </Box>
            </Box>
          )}
      </div>

      {/* Video Modal */}
      {(videoModal.isOpen || videoModal?.isScanned) && (
        <Grow in={videoModal.isOpen || videoModal?.isScanned}>
          <Box
            sx={{
              position: "absolute",
              top: "25%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1300,
              width: "18vw",
              height: "32vw",
              backgroundColor: "transparent",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                pointerEvents: "auto",
                width: "100%",
                height: "100%",
              }}
            >
              <Box display="flex" justifyContent="flex-end">
                <IconButton
                  onClick={handleClose}
                  sx={{
                    marginBottom: ".75vw",
                    padding: ".63vw .6vw .63vw .65vw",
                    background:
                      "linear-gradient(180deg, #0D0D0D 0%, #080808 100%)",
                  }}
                >
                  {getIcon("Cross")}
                </IconButton>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  backgroundColor: "#A7E1F8",
                  borderRadius: ".25vw",
                  width: "100%",
                  height: "100%",
                }}
              >
                <video
                  ref={videoRef}
                  muted={isMuted}
                  loop
                  autoPlay
                  onTimeUpdate={updateProgress}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                    borderRadius: ".25vw",
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <source src={videoModal?.data} type="video/mp4" />
                </video>

                <IconButton
                  onClick={togglePlayPause}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    color: "#fff",
                    padding: ".5vw",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.5)" },
                  }}
                >
                  {isPlaying ? (
                    <Pause style={{ width: "2vw", height: "2vw" }} />
                  ) : (
                    <PlayArrow style={{ width: "2vw", height: "2vw" }} />
                  )}
                </IconButton>

                <IconButton
                  onClick={toggleMute}
                  sx={{
                    width: "1.8vw",
                    height: "1.8vw",
                    position: "absolute",
                    bottom: ".75vw",
                    right: ".5vw",
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.5)" },
                  }}
                >
                  {isMuted ? (
                    <VolumeOff style={{ width: "1.5vw", height: "1.5vw" }} />
                  ) : (
                    <VolumeUp style={{ width: "1.5vw", height: "1.5vw" }} />
                  )}
                </IconButton>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: ".2vw",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    cursor: "pointer",
                    borderBottomLeftRadius: ".25vw",
                    borderBottomRightRadius: ".25vw",
                    overflow: "hidden",
                  }}
                  onClick={handleProgressClick}
                >
                  <Box
                    sx={{
                      width: `${progress}%`,
                      height: "100%",
                      backgroundColor: "#fff",
                      transition: "width 0.1s linear",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Grow>
      )}
    </>
  );
};

export default HomeScreen;
